import cx from 'classnames';

import type { FontFamilyType } from '~/interfaces/font_family';
import normalizeFontType from '~/utils/normalizeFontType';

// TODO: Remove legacy text components overtime, this will be the component to use for all text.

interface Props {
  as?: 'p' | 'span' | 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'div';
  children?: any;
  className?: string;
  color?: string;
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | '4xl' | '5xl' | '6xl';
  weight?: 'extralight' | 'light' | 'regular' | 'medium' | 'semibold' | 'bold' | 'black';

  // Font Families should match configuration in tailwind.config.js
  fontFamily?: FontFamilyType | string;
  fontFamilyv2?: string;
}

const FONT_OPTIONS: any = {
  ariel: 'font-ariel',
  bitter: 'font-bitter',
  courier: 'font-courier',
  didot: 'font-didot',
  garamond: 'font-garamond',
  georgia: 'font-georgia',
  helvetica: 'font-helvetica',
  lora: 'font-lora',
  'lucida-console': 'font-lucida-console',
  merriweather: 'font-merriweather',
  monaco: 'font-monaco',
  montserrat: 'font-montserrat',
  'open-sans': 'font-open_sans',
  palatino: 'font-palatino',
  'playfair-display': 'font-playfair-display',
  poppins: 'font-poppins',
  roboto: 'font-roboto',
  'times-new-roman': 'font-times-new-roman',
  'trebuchet-ms': 'font-trebuchet-ms',
  verdana: 'font-verdana',
};

const Typography = ({
  as = 'p',
  children,
  size = 'md',
  weight = 'regular',
  className = '',
  fontFamily = 'ariel',
  color,
  fontFamilyv2,
}: Props) => {
  const normalizedFont = normalizeFontType(fontFamily);
  const fontClass = FONT_OPTIONS?.[`${normalizedFont}`] || '';
  const style: any = {};
  const fallbackHeaderFontClass = fontClass ? '' : 'wt-header-font';
  const fallbackBodyFontClass = fontClass ? '' : 'wt-body-font';

  // Edge case for font-family Garamond which is not web safe across all browsers
  // We should have a more robost solution for this font long term but this was an easy holdover to resolve the user problem for now.
  const isGaramond = normalizedFont === 'garamond' || fontFamilyv2 === 'Garamond';

  // Inject styles into style object if needed
  if (color) style.color = color;
  if (fontFamilyv2 && !isGaramond)
    style.fontFamily = `${fontFamilyv2}, sans-ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto,"Helvetica Neue", Arial, "Noto Sans", sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"`;

  const textClasses = (type: 'header' | 'body') =>
    cx(
      className,

      // Sizing
      size === 'xs' && 'text-xs sm:text-sm',
      size === 'sm' && 'text-sm',
      size === 'md' && 'text-md',
      size === 'lg' && 'text-md sm:text-lg',
      size === 'xl' && 'text-lg sm:text-xl',
      size === '2xl' && 'text-xl sm:text-2xl',
      size === '3xl' && 'text-2xl sm:text-3xl',
      size === '4xl' && 'text-3xl sm:text-4xl',
      size === '5xl' && 'text-4xl sm:text-5xl',
      size === '6xl' && 'text-4xl sm:text-6xl',

      // Weight
      weight === 'extralight' && 'font-extralight',
      weight === 'light' && 'font-light',
      weight === 'regular' && 'font-regular',
      weight === 'medium' && 'font-medium',
      weight === 'semibold' && 'font-semibold',
      weight === 'bold' && 'font-bold',
      weight === 'black' && 'font-black',

      // Font Family
      isGaramond && 'font-garamond',
      fontClass,
      type === 'header' && fallbackHeaderFontClass,
      type === 'body' && fallbackBodyFontClass,
    );

  switch (as) {
    case 'p':
      return (
        <p style={style} className={textClasses('body')}>
          {children}
        </p>
      );
    case 'div':
      return (
        <div style={style} className={textClasses('body')}>
          {children}
        </div>
      );
    case 'span':
      return (
        <span style={style} className={textClasses('body')}>
          {children}
        </span>
      );
    case 'h1':
      return (
        <h1 style={style} className={textClasses('header')}>
          {children}
        </h1>
      );
    case 'h2':
      return (
        <h2 style={style} className={textClasses('header')}>
          {children}
        </h2>
      );
    case 'h3':
      return (
        <h3 style={style} className={textClasses('header')}>
          {children}
        </h3>
      );
    case 'h4':
      return (
        <h4 style={style} className={textClasses('header')}>
          {children}
        </h4>
      );
    case 'h5':
      return (
        <h5 style={style} className={textClasses('header')}>
          {children}
        </h5>
      );
    case 'h6':
      return (
        <h6 style={style} className={textClasses('header')}>
          {children}
        </h6>
      );
    default:
      return (
        <p style={style} className={textClasses('body')}>
          {children}
        </p>
      );
  }
};

export default Typography;
